import { ApiResult } from '@mam/main/ApiHooks';
import { dateFormatter } from '../utils';

import type {
	CountryResponse,
	ErpQuadrantsResponse,
	ErpRevenueResponse,
	ErpStatsResponse,
	PackMonthDataResponse,
	PriceChangeResponse,
	VirtualPackErpResponse,
} from 'com.cogvio.pricemonitor.api';
import { Failure, Loading } from '@mam/main/Result';

export const parseERPApiResult = (
	erpApiResult: VirtualPackErpResponse | null | undefined,
	apiResult?: ApiResult<any, any>
) => {
	const stats: ErpStatsResponse = erpApiResult?.stats || { quadrants: [], cumulativeRevenues: [] };
	const quadrantStats = stats.quadrants.reduce(
		(aggregator: { [countryCode: string]: ErpQuadrantsResponse }, stat) => ({
			...aggregator,
			[stat.country.alpha2]: stat,
		}),
		{}
	);
	const cumulativeStats = stats.cumulativeRevenues.reduce(
		(aggregator: { [countryCode: string]: ErpRevenueResponse }, stat) => ({
			...aggregator,
			[stat.country.alpha2]: stat,
		}),
		{}
	);

	const byMonth: Record<string, PackMonthDataResponse[]> =
		erpApiResult?.monthData.reduce(
			(aggregator: { [date: string]: PackMonthDataResponse[] }, data) => ({
				...aggregator,
				[data.priceDate]: (aggregator[data.priceDate] || []).concat([data]),
			}),
			{}
		) || {};

	const byPrice =
		erpApiResult?.monthData.reduce(
			(aggregator: { [price: string]: PackMonthDataResponse[] }, data) => ({
				...aggregator,
				...(data.revenue !== null ? { [data.price.value]: (aggregator[data.price.value] || []).concat([data]) } : {}),
			}),
			{}
		) || {};

	const byCountry =
		erpApiResult?.monthData.reduce(
			(aggregator: { [countryCode: string]: PackMonthDataResponse['country'] & PackMonthDataResponse }, data) => ({
				...aggregator,
				[data.country.alpha2]: {
					...data,
					...data.country,
				},
			}),
			{}
		) || {};

	const allDataByCountry =
		erpApiResult?.monthData.reduce(
			(aggregator: { [countryCode: string]: PackMonthDataResponse[] }, data) => ({
				...aggregator,
				[data.country.alpha2]: (aggregator[data.country.alpha2] || []).concat([data]),
			}),
			{}
		) || {};

	const erpImpactedCountries =
		erpApiResult?.erpImpacted.reduce(
			(aggregator: { [countryCode: string]: CountryResponse }, data) => ({
				...aggregator,
				[data.name]: data,
			}),
			{}
		) || {};

	const sortedDates = Object.keys(byMonth).sort((aDate, bDate) =>
		new Date(aDate).getTime() < new Date(bDate).getTime() ? -1 : 1
	);

	const priceChangesByMonth =
		erpApiResult?.priceChanges.reduce(
			(aggregator: { [date: string]: PriceChangeResponse[] }, data) => ({
				...aggregator,
				[dateFormatter(new Date(data.date))]: (aggregator[dateFormatter(new Date(data.date))] || []).concat([data]),
			}),
			{}
		) || {};

	const currency = erpApiResult?.monthData[0]?.price.currency || '';

	return {
		allDataByCountry,
		byCountry,
		byPrice,
		byMonth,
		currency,
		erpImpactedCountries,
		priceChangesByMonth,
		sortedDates,
		loading: apiResult instanceof Loading,
		failure: apiResult instanceof Failure,
		errorCodes: apiResult?.error?.codes || new Set<string>(),
		stats,
		quadrantStats,
		cumulativeStats,
	};
};
